import { CodeInputLight } from '@/components/code-input/code-input.light';
import { ErrorSidePanel } from '@/components/errors';
import { useSendCode, useValidateCode } from '@/hooks/api/contact-validation.hooks';
import { loadHistoryState } from '@/lib';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  ErrorCodes,
  logger,
  templates,
  useI18n,
  widgets,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import z from 'zod';
import { codeInputStateSchema } from '../-model/flow';
import { useUpdateLoginOptions } from './-hooks/login-options.hooks';

export const Route = createFileRoute(
  '/(home)/home/(change-contacts)/_sheet/change-contacts/code-input/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: loadHistoryState(codeInputStateSchema, { errorCode: ErrorCodes.AccessDenied }),
});

function RouteComponent() {
  const { t } = useI18n();
  const { type, contact } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const isPhone = type === 'phone';
  const contactType = isPhone ? 'Sms' : 'Email';

  const form = useForm<{ code: string }>(
    {
      code: {
        value: '',
        id: 'validate-contact-otp',
        validator: z
          .string()
          .length(
            widgets.ValidationCodeLength,
            t('Common.ContactValidation.CodeInput.Errors.InvalidCode'),
          ),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { sendCode, isSending, captcha } = useSendCode();

  const { mutate: updateLoginOptions, isPending: isUpdating } = useUpdateLoginOptions({
    onSuccess: () => {
      navigate({ to: '/home' });
    },
  });

  const { validateCode, isValidating } = useValidateCode({
    onSuccess: () => {
      updateLoginOptions({
        aliases: [
          {
            Username: contact,
            Type: isPhone ? 'Mobile' : 'Email',
          },
        ],
        securityCodes: [
          {
            TargetId: contact,
            Type: contactType,
            Code: form.value.code,
          },
        ],
      });
    },
    onError: () => {
      form.addError('code', t('Common.ContactValidation.CodeInput.Errors.InvalidCode'));
      form.setValue('code', '');
    },
  });

  const handleResend = () => {
    form.setValue('code', '');
    if (captcha.challenge && !captcha.token) return;
    sendCode({ targetId: contact, type: contactType, resend: true, captcha });
  };

  const handleSubmit = ({ code }: typeof form.value) => {
    validateCode({ code, targetId: contact, contactType });
  };

  const handleCodeChange = (value: string) => {
    form.setValue('code', value);

    if (value.length === widgets.ValidationCodeLength) {
      form.updateValidity();
      validateCode({ code: value, targetId: contact, contactType });
    }
  };

  const isProcessing = isValidating || isUpdating;

  return templates.getSkinSelector(skinConfig());

  function skinConfig(): SkinPageConfig {
    return {
      skins: [],
      defaultValue: (
        <CodeInputLight
          translations={{
            resendCode: 'Home.ChangeContacts.ValidateCode.ResendCode',
            confirm: 'Home.ChangeContacts.ValidateCode.Confirm',
          }}
          codeValue={form.value.code}
          codeId={form.ids.code ?? 'validate-contact-otp'}
          codeErrors={form.errors.code}
          isValidating={isProcessing}
          isSending={isSending}
          captchaChallenge={captcha.challenge}
          onCaptchaChange={captcha.setToken}
          onCodeChange={handleCodeChange}
          onResend={handleResend}
          onSubmit={form.handleSubmit(handleSubmit)}
        />
      ),
    };
  }
}
